import type {
  ChangeEventHandler,
  ComponentProps,
  InputHTMLAttributes,
  ReactNode,
} from 'react';
import styled from 'styled-components';

import { TextInput } from '#src/atoms/inputs/TextInput/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import { LabelS } from '#src/atoms/typography/index.tsx';
import { Field } from '#src/components/form/Form/Field/index.tsx';
import { FormLabel } from '#src/components/form/FormLabel/index.tsx';

const Root = styled(Vertical).attrs({ gap: 0.5 })`
  width: 100%;
`;

export type Props = {
  id?: string;
  name: string;
  placeholder?: string;
  className?: string;
  rows?: number;
  label?: ReactNode;
  required?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  helperText?: string;
  endAdornment?: ReactNode;
  withoutAsterisk?: boolean;
  validate?: (value: string) => string | null;
  defaultValue?: string;
  startAdornment?: ReactNode;
  size?: ComponentProps<typeof TextInput>['size'];
} & Partial<Omit<InputHTMLAttributes<HTMLInputElement>, 'size'>>;

const LabelText = ({
  label,
  error,
  required,
  withoutAsterisk,
}: {
  label: ReactNode;
  error: string | null;
  required?: boolean;
  withoutAsterisk?: boolean;
}) => (
  <>
    {label} {!!required && !withoutAsterisk && '* '}
    {!!error && <span className="error">{error || null}</span>}
  </>
);

export const TextField = ({
  id,
  defaultValue = '',
  onChange = () => {},
  name,
  label,
  placeholder,
  required,
  className,
  helperText,
  withoutAsterisk,
  validate,
  ...rest
}: Props) => (
  <Field
    defaultValue={defaultValue}
    name={name}
    validate={validate}
    render={({ error, handleChange, value, disabled }) => {
      const fieldId = id || name;
      const helperTextId = `${fieldId}-helper`;

      return (
        <Root>
          {!!label && (
            <FormLabel id={fieldId}>
              <LabelText label={label} error={error} required={required} />
            </FormLabel>
          )}
          <TextInput
            className={className}
            error={!!error}
            id={fieldId}
            aria-describedby={helperText ? helperTextId : undefined}
            placeholder={
              placeholder
                ? `${placeholder}${
                    required && !label && !withoutAsterisk ? ' *' : ''
                  }`
                : undefined
            }
            value={value}
            required={required}
            onChange={e => {
              onChange?.(e);
              handleChange(e.target.value, e.target);
            }}
            {...rest}
            disabled={disabled || rest.disabled}
          />
          {!!helperText && (
            <LabelS id={helperTextId} color="var(--c-onBase-secondary)">
              {helperText}
            </LabelS>
          )}
        </Root>
      );
    }}
  />
);
